<template>
  <div class="my-orders-page">
    <div class="container">

      <div v-if="isLoading" class="loading-state">
        <el-skeleton :rows="5" animated />
        <el-skeleton :rows="5" animated style="margin-top: 20px;" />
      </div>

      <div v-else-if="fetchError" class="error-state">
        <el-result icon="error" title="訂單加載失敗" :sub-title="fetchError">
          <template #extra>
            <el-button type="primary" @click="fetchOrders">重試</el-button>
            <el-button @click="goToHome">返回首頁</el-button>
          </template>
        </el-result>
      </div>

      <div v-else-if="orders.length === 0" class="empty-orders-state">
        <el-empty description="您還沒有任何訂單記錄">
          <el-button type="primary" @click="goToShopping">馬上去逛逛</el-button>
        </el-empty>
      </div>

      <div v-else class="orders-list-container">
        <el-card v-for="order in orders" :key="order.id" class="order-card" shadow="hover">
          <template #header>
            <div class="order-card-header">
              <div class="header-left">
                <span class="order-sn-label">訂單號：</span>
                <span class="order-sn-value">{{ order.platform_order_id }}</span>
                <el-tag :type="getOrderStatusTagType(order.status)" size="small" class="order-status-tag">
                  {{ order.statusText || order.status }}
                </el-tag>
              </div>
              <div class="header-right">
                <span class="order-time">{{ formatOrderTime(order.orderTime) }}</span>
                <!-- ****** 提示信息保持原样 ****** -->
                <span v-if="order.pay_methods === 'offline' && order.isWithinCancellationPeriod && order.status === 'processing'" class="cancellation-tip">
                  <el-icon><InfoFilled /></el-icon> 下單3小時內可取消訂單
                </span>
              </div>
            </div>
          </template>

          <div class="order-card-body">
            <div v-for="item in order.goods_info" :key="item.id || item.product_id" class="order-item-row">
              <img :src="`${backendBaseUrl}/${item.main_image}`" :alt="item.productName" class="item-image" @error="onImageError"/>
              <div class="item-details">
                <p class="item-name">{{ item.name }}</p>
                <p class="item-sku" v-if="item.sku">{{ item.sku }}</p>
              </div>
              <div class="item-price">${{ item.price_at_purchase.toLocaleString() }}</div>
              <div class="item-quantity">x {{ item.quantity }}</div>
            </div>
            <div v-if="order.goods_info.length > maxItemsToShowInitially" class="more-items-indicator">
              ... 等共 {{ order.goods_info.length }} 件商品
            </div>
          </div>

          <div class="order-card-summary">
            <div class="summary-item">
              <span class="summary-label">支付方式：</span>
              <span class="summary-value">{{ getPaymentMethodText(order.pay_methods) }}</span>
            </div>

            <div class="summary-item">
              <span class="summary-label">配送方式：</span>
              <span class="summary-value">{{ getDeliveryMethodText(order.delivery_method) }}</span>
            </div>

            <!-- ========= 修改開始：優化收貨人信息排版 ========= -->
            <div class="summary-item" v-if="order.user_name">
              <span class="summary-label">收貨人：</span>
              <div class="recipient-details-container">
                <!-- 第一行：顯示姓名、電話、郵箱（如果存在） -->
                <div>
                  <span class="summary-value">{{ order.user_name }}</span>
                  <template v-if="order.user_phone">
                    <span> / </span>
                    <span class="summary-value">{{ order.user_phone }}</span>
                  </template>
                  <template v-if="order.user_email">
                    <span> / </span>
                    <span class="summary-value">{{ order.user_email }}</span>
                  </template>
                </div>
                <!-- 第二行：單獨顯示地址（如果存在） -->
                <div v-if="order.user_address" class="summary-value recipient-address-line">
                  {{ order.user_address }}
                </div>
              </div>
            </div>
            <!-- ========= 修改結束：優化收貨人信息排版 ========= -->

            <div class="summary-item order-total">
              <span class="summary-label">訂單總額：</span>
              <span class="summary-value total-amount">${{ order.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}</span>
            </div>
          </div>

          <div class="order-card-logistics" v-if="order.logistics">
            <el-icon><Van /></el-icon>
            <span>物流信息：{{ order.logistics }} - {{ order.logisticsNumber }}</span>
            <!-- 可以增加一個複製單號按鈕或跳轉查詢鏈接 -->
          </div>
          <div class="order-card-logistics" v-else>
             <el-icon><Clock /></el-icon>
            <span>物流信息：等待攬收或發貨</span>
          </div>


          <div class="order-card-actions">
            <!-- <el-button type="primary" plain size="small" @click="viewOrderDetail(order.id)">訂單詳情</el-button>
            <el-button
              v-if="canRebuy(order.status)"
              type="default"
              size="small"
              @click="rebuyOrder(order)"
            >
              再次購買
            </el-button> -->
             <el-button
              v-if="canCancel(order)"
              type="danger"
              plain
              size="small"
              @click="cancelOrder(order.id)"
            >
              取消訂單
            </el-button>
            <!-- <el-button
              v-if="canConfirmReceipt(order.status)"
              type="success"
              size="small"
              @click="confirmReceipt(order.id)"
            >
              確認收貨
            </el-button> -->
          </div>
        </el-card>

        <el-pagination
          v-if="totalOrders > 0"
          class="orders-pagination"
          background
          layout="prev, pager, next, jumper, ->, total"
          :total="totalOrders"
          v-model:current-page="currentPage"
          :page-size="pageSize"
          @current-change="handlePageChange"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Van, Clock, InfoFilled } from '@element-plus/icons-vue'; // 引入 InfoFilled 图标
import OrderService from '../../services/UserOrder'; // 假设你的订单服务路徑
// import orderService from '../../services/auth'; // 使用你提供的路徑
import orderService from './mock/MyOrdermock';


const router = useRouter();
const backendBaseUrl = process.env.VUE_APP_BACKEND_BASE_URL;

const isLoading = ref(true);
const fetchError = ref(null);
const orders = ref([]);
const totalOrders = ref(0);
const currentPage = ref(1);
const pageSize = ref(5); // 每頁顯示多少訂單

const maxItemsToShowInitially = 2; // 每個訂單卡片初始顯示的商品數量

const fetchOrders = async () => {
  isLoading.value = true;
  fetchError.value = null;
  try {
    // 假設 orderService.getUserOrders 接收分頁參數
    const response = await OrderService.getOrders({
      page: currentPage.value,
      page_size: pageSize.value,
    });
    // 假設後端返回的數據結構是 { orders: [], total: 0 }
    // 或者直接是訂單數組，總數需要額外獲取或在響應頭中
    if (response && Array.isArray(response.items)) { // 根據你後端API的實際返回調整
        orders.value = response.items.map(order => ({
            ...order,
            // 如果後端沒有直接提供 statusText，可以在前端映射
            statusText: getOrderStatusText(order.status),
            // 假設 shippingAddress 和 logisticsInfo 已經是期望的結構
            // 这个 isWithinCancellationPeriod 是后端计算后传递给前端的布尔值
            isWithinCancellationPeriod: checkCancellationPeriod(order.orderTime, order.status),
        }));
        totalOrders.value = response.total || response.items.length; // 優先使用後端返回的 total
    } else if (Array.isArray(response)){ // 如果直接返回數組
        orders.value = response.map(order => ({
            ...order,
            statusText: getOrderStatusText(order.status),
            isWithinCancellationPeriod: checkCancellationPeriod(order.orderTime, order.status),
        }));
        totalOrders.value = response.length; // 這種情況下可能沒有總數，或需要前端自己記
    } else {
        console.warn("獲取的訂單數據格式不符合預期:", response);
        orders.value = [];
        totalOrders.value = 0;
    }

  } catch (error) {
    console.error("獲取用戶訂單失敗:", error);
    fetchError.value = error.response?.data?.detail || error.message || "無法加載訂單數據，請稍後再試。";
    orders.value = [];
    totalOrders.value = 0;
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  fetchOrders();
});

const handlePageChange = async (newPage) => {
  currentPage.value = newPage;
  fetchOrders();
};

const getOrderStatusText = (status) => {
  const statusMap = {
    pending_payment: '待付款',
    processing: '處理中',
    shipped: '已發貨',
    delivered: '已付款',
    completed: '已完成',
    cancelled: '已取消',
    refunded: '已退款',
  };
  return statusMap[status] || status;
};

const getPaymentMethodText = (method) => {
    const paymentMap = {
        'ct_payment': 'CT pay',
        'credit_card': '信用卡支付',
        'offline': '貨到付款',
        'atm': 'ATM付款',
    };
    return paymentMap[method] || method;
}

const getDeliveryMethodText = (method) => {
    const paymentMap = {
      'seven-eleven': '7-11超商',
      'family-mart': '全家超商',
      'home-delivery': '宅配到府'
    };
    return paymentMap[method] || method;
}

const getOrderStatusTagType = (status) => {
  const typeMap = {
    pending_payment: 'warning',
    processing: 'primary',
    shipped: 'info',
    delivered: 'success',
    completed: 'success',
    cancelled: 'danger',
    refunded: 'info',
  };
  return typeMap[status] || 'default';
};

const formatOrderTime = (dateTimeString) => {
  if (!dateTimeString) return 'N/A';
  try {
    const date = new Date(dateTimeString);
    return date.toLocaleString('zh-CN', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit'
    }).replace(/\//g, '-');
  } catch (e) {
    console.error("時間格式化錯誤:", dateTimeString, e);
    return dateTimeString;
  }
};

const onImageError = (event) => {
  // 處理圖片加載失敗，可以替換為預設圖片
  event.target.src = `${backendBaseUrl}/static/louis_vuitton衣服/1748269351384_82622_medium.jpg`; // 修改為你的預設圖片路徑
};

// const viewOrderDetail = (orderId) => {
//   // 跳轉到訂單詳情頁，你需要創建這個頁面和路由
//   router.push({ name: 'OrderDetail', params: { orderId } }); // 假設路由名為 OrderDetail
//   ElMessage.info(`查看訂單 ${orderId} 詳情 (功能待實現)`);
// };

// const rebuyOrder = (order) => {
//   // 將訂單中的商品重新加入購物車的邏輯
//   // 這可能需要遍歷 order.items，並調用 addToCart 的服務
//   ElMessage.info(`再次購買訂單 ${order.orderSn} 中的商品 (功能待實現)`);
//   // 示例：
//   // order.items.forEach(item => {
//   //   cartService.addItem({ productId: item.productId, quantity: item.quantity, sku: item.sku });
//   // });
//   // router.push({ name: 'Cart' });
// };

const cancelOrder = async (orderId) => {
  try {
    await ElMessageBox.confirm('確定要取消此訂單嗎？', '取消訂單', {
      confirmButtonText: '確定取消',
      cancelButtonText: '再想想',
      type: 'warning',
    });
    await OrderService.cancelUserOrder(orderId); // 假設有此服務方法
    ElMessage.success('訂單已成功取消');
    fetchOrders(); // 刷新訂單列表
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error(error.detail || '取消訂單失敗');
    }
  }
};

// const confirmReceipt = async (orderId) => {
//    try {
//     await ElMessageBox.confirm('請確認您已收到貨物。此操作不可逆。', '確認收貨', {
//       confirmButtonText: '確認收到',
//       cancelButtonText: '還沒有',
//       type: 'warning',
//     });
//     await orderService.confirmOrderReceipt(orderId); // 假設有此服務方法
//     ElMessage.success('已確認收貨！感謝您的購買。');
//     fetchOrders(); // 刷新訂單列表
//   } catch (error) {
//     if (error !== 'cancel') {
//       ElMessage.error(error.response?.data?.detail || '確認收貨失敗');
//     }
//   }
// };


const checkCancellationPeriod = (orderTime, status) => {
    if (status !== 'processing') return false; // 只允許待付款的訂單
    if (!orderTime) return false;

    try {
        const orderDate = new Date(orderTime);
        const now = new Date();
        // 計算時間差（毫秒）
        const timeDiffMs = now.getTime() - orderDate.getTime();
        // 3 小時 = 3 * 60 * 60 * 1000 毫秒
        const cancellationLimitMs = 3 * 60 * 60 * 1000;
        return timeDiffMs < cancellationLimitMs;
    } catch (e) {
        console.error("檢查取消時間時發生錯誤:", orderTime, e);
        return false; // 格式錯誤或解析失敗則不允許取消
    }
};


const canCancel = (order) => {
    // 顯示取消訂單按鈕的條件是：
    // 1. 訂單狀態是 'processing'
    // 2. 支付方式是 'offline'
    // 3. 並且訂單仍在可取消的時間範圍內 (由后端傳遞的 isWithinCancellationPeriod 布爾值控制)
    return order.status === 'processing' &&
           order.pay_methods === 'offline' &&
           order.isWithinCancellationPeriod; // 這個布爾值由后端從時間計算後傳遞過來
};
// const canConfirmReceipt = (status) => status === 'shipped';

const goToHome = () => {
  router.push('/');
};

const goToShopping = () => {
  router.push('/shoes'); // 或你的商品列表頁
};

</script>

<style scoped lang="scss">
.my-orders-page {
//   background-color: #f7f8fa;
  min-height: calc(100vh - 72px); // 假設頂部導航欄高度
  padding: 1.5rem 0 3rem;
}

.container {
  max-width: 1200px; // 訂單頁面可以寬一些
  margin: 0 auto;
  padding: 0 1rem;
}

.loading-state, .error-state, .empty-orders-state {
  display: flex;
  flex-direction: column; // 讓內容垂直排列
  align-items: center;
  justify-content: center;
  min-height: 400px;
  background-color: #fff;
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  .el-button {
    margin-top: 1.5rem;
  }
}
.error-state .el-button + .el-button { // 重試和返回首頁按鈕間距
    margin-left: 10px;
}


.orders-list-container {
  display: flex;
  flex-direction: column;
  gap: 1.25rem; // 卡片之間的間距
}

.order-card {
  border-radius: 8px;
  border: 1px solid #e9ebf0;
  background-color: #fff;
  transition: box-shadow 0.3s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  }

  :deep(.el-card__header) {
    background-color: #f9fafb; // 淺灰色頭部背景
    padding: 0.8rem 1.25rem;
    border-bottom: 1px solid #e9ebf0;
  }
  :deep(.el-card__body) {
    padding: 1.25rem;
  }
}

.order-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.9rem;

  .header-left {
    display: flex;
    align-items: center;
    color: #606266;
    .order-sn-label {
        // font-weight: 500;
    }
    .order-sn-value {
      color: #303133;
      font-weight: 500;
      margin-right: 10px;
    }
    .order-status-tag {
      // margin-left: auto; // 如果希望標籤靠右一點
    }
  }
  .header-right {
    color: #909399;
    font-size: 0.85rem;
    /* 优化提示信息样式 */
    display: flex; /* 使用 flex 布局来对齐时间与提示 */
    align-items: center;
    white-space: nowrap; /* 防止时间或提示信息换行 */

    .order-time {
      margin-right: 10px; /* 时间和提示信息之间的间隔 */
    }

    .cancellation-tip { /* 优化后的提示样式 */
      display: flex;
      align-items: center;
      color: #ff9900; /* 使用橙色作为提示颜色 */
      font-size: 0.85rem; /* 稍微大一点的字号 */
      font-weight: 500;  /* 加粗字体 */
      margin-left: 8px; /* 和时间保持一定距离 */
    }
    .cancellation-tip .el-icon {
      margin-right: 4px;
      color: #ff9900;
      font-size: 1.1em; /* 图标稍大 */
    }
  }
}

.order-card-body {
  padding-bottom: 1rem; // 與下方摘要區域留出空間
  border-bottom: 1px solid #f0f2f5; // 分隔線
  margin-bottom: 1rem;

  .order-item-row {
    display: flex;
    align-items: flex-start; // 頂部對齊，以防商品名多行
    gap: 1rem;
    padding: 0.75rem 0;
    border-bottom: 1px dashed #f5f7fa;
    &:last-child {
      border-bottom: none;
      padding-bottom: 0;
    }
    &:first-child {
        padding-top: 0;
    }
  }

  .item-image {
    width: 60px;
    height: 60px;
    object-fit: cover;
    border-radius: 4px;
    border: 1px solid #e9ebf0;
    flex-shrink: 0;
  }

  .item-details {
    flex-grow: 1;
    min-width: 0; // 防止文字溢出
    .item-name {
      font-size: 0.9rem;
      color: #303133;
      font-weight: 500;
      margin: 0 0 0.25rem 0;
      line-height: 1.4;
      // 多行省略 (可選)
      // display: -webkit-box;
      // -webkit-line-clamp: 2;
      // -webkit-box-orient: vertical;
      // overflow: hidden;
      // text-overflow: ellipsis;
      word-break: break-all; // 強制換行
    }
    .item-sku {
      font-size: 0.8rem;
      color: #909399;
    }
  }
  .item-price, .item-quantity {
    font-size: 0.9rem;
    color: #606266;
    text-align: right;
    flex-shrink: 0;
    white-space: nowrap;
  }
  .item-price { min-width: 70px; }
  .item-quantity { min-width: 40px; margin-left:15px;}
}

.more-items-indicator {
  font-size: 0.85rem;
  color: #909399;
  text-align: right;
  padding-top: 0.5rem;
}

.order-card-summary {
  display: flex;
  flex-wrap: wrap; // 允許換行
  justify-content: flex-end; // 主要內容靠右
  align-items: center;
  gap: 1rem; // 項目間的間距
  font-size: 0.9rem;
  color: #606266;
  margin-bottom: 1rem;

  .summary-item {
    display: flex;
    align-items: center; /* 保持原有的 align-items: center */
    .summary-label {
        margin-right: 5px;
    }
    .summary-value {
      color: #303133;
      font-weight: 500;
    }
    &.order-total .total-amount {
      font-size: 1.1rem;
      color: #f56c6c; // 強調總價
      font-weight: bold;
    }
  }
}

/* 新增的收貨人信息相關樣式（僅用於結構，無實際樣式添加，依賴現有 summary-value） */
.recipient-details-container {
  display: flex;
  flex-direction: column; /* 让地址和联系信息分开显示 */
}
.recipient-address-line {
  margin-top: 4px; /* 给地址行增加一点顶部间距 */
}


.order-card-logistics {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
  color: #67c23a; // 綠色表示物流信息
  background-color: #f0f9eb;
  padding: 8px 12px;
  border-radius: 4px;
  margin-bottom: 1rem;
  border: 1px solid #e1f3d8;
  .el-icon {
    margin-right: 6px;
    font-size: 1.1em;
  }
  & span:first-of-type { // 物流公司和單號之間的連接符
      margin-right: 5px;
  }
}
.order-card-logistics.waiting { // 等待發貨狀態
    color: #e6a23c;
    background-color: #fdf6ec;
    border-color: #faecd8;
}


.order-card-actions {
  display: flex;
  justify-content: flex-end; // 按鈕靠右
  gap: 0.75rem;
  padding-top: 1rem;
  border-top: 1px solid #f0f2f5;
}

.orders-pagination {
  margin-top: 2rem;
  display: flex; // 使用 flex 讓 -> 生效
  justify-content: flex-end; // 默認靠右，但 -> 會推到左邊
}


@media (max-width: 768px) {
  .container { padding: 0 0.75rem; }

  .order-card {
    :deep(.el-card__header) { padding: 0.7rem 1rem; }
    :deep(.el-card__body) { padding: 1rem; }
  }
  .order-card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.3rem;
    font-size: 0.85rem;
    .header-right {
      align-self: flex-end;
      /* 优化小屏幕下时间与提示信息的布局 */
      display: flex;
      flex-wrap: wrap; /* 允许换行 */
      justify-content: flex-end; /* 右对齐 */
      width: 100%; /* 占满宽度以便居中或右对齐 */
      margin-top: 5px; /* 与上面内容留出一点间隔 */
    }
    .header-right .order-time {
        /* margin-right: 10px; */ /* 移除右边距，因为手机上可能不需要 */
        margin-bottom: 5px; /* 时间和提示信息之间增加底部间距 */
    }
    .cancellation-tip { /* 优化小屏幕下的提示样式 */
        /* margin-left: 0; */ /* 移除左边距 */
        width: 100%; /* 让提示信息独占一行 */
        justify-content: center; /* 居中显示提示信息 */
        font-size: 0.8rem; /* 字体稍小一些 */
        font-weight: normal; /* 恢复正常字重 */
    }
    .cancellation-tip .el-icon {
        font-size: 1em; /* 图标大小恢复正常 */
    }
  }
  .order-card-body .order-item-row {
    gap: 0.75rem;
    .item-image { width: 50px; height: 50px; }
    .item-details .item-name { font-size: 0.85rem; }
    .item-price, .item-quantity { font-size: 0.85rem; }
  }
  .order-card-summary {
    font-size: 0.85rem;
    justify-content: space-between; // 在小屏幕上两端对齐可能更好
    .summary-item.order-total .total-amount { font-size: 1rem; }
  }
  .order-card-actions {
    flex-wrap: wrap; // 允许按钮换行
    justify-content: flex-start; // 小屏幕上按钮靠左开始排列
    gap: 0.5rem;
  }
}

@media (max-width: 480px) {
    .order-card-body .order-item-row {
        // 可以考虑更紧凑的布局，例如商品名和 SKU 在图片右侧，价格和数量在下方
    }
}
/* ****** 我添加的提示信息的样式优化 ****** */
.cancellation-tip {
  display: flex;
  align-items: center;
  color: #ff9900; /* 使用橙色作为提示颜色 */
  font-size: 0.85rem; /* 稍微大一点的字号 */
  font-weight: 500;  /* 加粗字体 */
  margin-left: 8px; /* 和时间保持一定距离 */
  white-space: nowrap; /* 防止换行 */
}
.cancellation-tip .el-icon {
  margin-right: 4px;
  color: #ff9900;
  font-size: 1.1em; /* 图标稍大 */
}
/* ****** 优化结束 ****** */
</style>